import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Shield, Truck, Award, Loader2, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { FreeToolsBar } from './FreeToolsBar';
import { getOptimizedImageUrl } from '../../utils/image';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero slides with promotional content
const heroSlides = [
  {
    title: 'Premium Spiritual Products',
    subtitle: 'Your Spiritual Journey Starts Here',
    description: 'Discover authentic gemstones, powerful rudraksha & sacred yantras energized by top astrologers.',
    cta: 'Shop Now',
    ctaLink: '/products',
    bgGradient: 'from-amber-900 via-amber-800 to-orange-900',
    image: 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/60ae3a4666448eba2736a2cc3cfb5dd741048307c757d428398bfd881ce70094.png',
  },
  {
    title: 'Free Kundli & Astrology',
    subtitle: '100% Free Tools',
    description: 'Generate detailed Kundli, check horoscope, matchmaking & more - completely free!',
    cta: 'Get Free Kundli',
    ctaLink: '/kundli',
    bgGradient: 'from-purple-900 via-indigo-800 to-purple-900',
    image: 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/b1fe8bf91c67236e9f0d5b7e1bb5a9ddf7544943bae7ad0ebb1d9badf433f408.png',
  },
  {
    title: 'Vastu Solutions',
    subtitle: 'Transform Your Space',
    description: 'Expert Vastu guidance with 16-zone compass analysis and personalized remedies.',
    cta: 'Explore Vastu',
    ctaLink: '/vastu-compass',
    bgGradient: 'from-emerald-900 via-teal-800 to-emerald-900',
    image: 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/844619927f71b05145abcb2aaccbbba389a640bd68c28d50b5de886228d5f08f.png',
  },
];

export const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    // Auto-rotate slides
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  const slide = heroSlides[currentSlide];

  return (
    <section className="relative" data-testid="hero-section">
      {/* Main Hero Banner */}
      <div className="relative h-[400px] lg:h-[500px] overflow-hidden">
        {/* Background Image with Overlay */}
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          <img
            src={getOptimizedImageUrl(slide.image, 1600)}
            alt={slide.title}
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${slide.bgGradient} opacity-80`} />
        </motion.div>

        {/* Content */}
        <div className="container mx-auto px-4 lg:px-8 h-full relative z-10">
          <div className="flex flex-col justify-center h-full max-w-2xl">
            <motion.div
              key={`content-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium mb-4 backdrop-blur-sm">
                {slide.subtitle}
              </span>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                {slide.title}
              </h1>
              <p className="text-white/90 text-base lg:text-lg mb-6 max-w-lg">
                {slide.description}
              </p>
              <Button
                asChild
                size="lg"
                className="gold-gradient text-white hover:opacity-90 rounded-full px-8 h-12 text-base font-semibold group shadow-lg"
                data-testid="hero-cta-btn"
              >
                <Link to={slide.ctaLink}>
                  {slide.cta}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center text-white transition-colors z-20"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center text-white transition-colors z-20"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                idx === currentSlide ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Free Tools Bar - Below Hero */}
      <FreeToolsBar />

      {/* Trust Badges Row */}
      <div className="bg-amber-50 py-3 border-b border-amber-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 lg:gap-8">
            {[
              { icon: Shield, text: '100% Authentic' },
              { icon: Award, text: 'Lab Certified' },
              { icon: Sparkles, text: 'Energized by Astrologers' },
              { icon: Truck, text: 'Pan-India Delivery' },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-2 text-sm text-amber-800"
              >
                <item.icon className="w-4 h-4 text-amber-600" />
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
